/**
 * S6 sisa (ISIAN §17, langkah 6) — pencari lot kain dua arah (keputusan koordinator 19/20 Sep 2026):
 *   "lot Y dipakai FA mana" di halaman ini (cari kode lot → pilih lot), dan
 *   "FA X memakai lot apa" lewat kartu `KartuLotFA` di detail FA.
 * Informasi saja — vonis Owner 19/20 Sep: tidak ada peringatan lot berbeda saat repeat order.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariLot, labelLot, ketLot } from "../../lib/sumberPilihan";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import Hint from "../../components/etams/Hint";
import { toast } from "sonner";

export function KartuLotFA({ faId }) {
  const [data, setData] = useState(null);
  const [sibuk, setSibuk] = useState(false);
  const muat = async () => {
    setSibuk(true);
    try { const r = await api.get(`/inventory/telusur-lot/fa/${faId}`); setData(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Lot kain gagal dimuat")); }
    finally { setSibuk(false); }
  };
  return (
    <Card className="p-4 space-y-2" data-testid="fa-lot-kain">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium">Lot kain yang dipakai</div>
        <Button size="sm" variant="outline" disabled={sibuk} onClick={muat} data-testid="fa-lot-muat">{data ? "Muat ulang" : "Tampilkan"}</Button>
      </div>
      {data && data.lot.length === 0 && (
        <div className="text-sm text-muted-foreground">
          {data.wo.length ? "Belum ada pengeluaran kain berlot untuk WO FA ini." : "FA ini belum masuk WO."}
        </div>
      )}
      {data && data.lot.length > 0 && (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground"><th>Bahan</th><th>Lot</th><th className="text-right">Qty</th><th>WO / pengeluaran</th></tr></thead>
          <tbody>
            {data.lot.map((l, i) => (
              <tr key={`${l.lot_id}-${i}`} className="border-t border-border">
                <td>{l.material}</td>
                <td className="font-mono text-xs"><Link to={`/inventory/telusur-lot?lot=${l.lot_id}`} className="underline">{l.lot_code}</Link></td>
                <td className="text-right tabular-nums">{l.qty}</td>
                <td className="text-xs">{l.wo_number} · {l.issue_number}{!l.keluar && " (belum keluar)"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data && data.wo.length > 1 && <div className="text-xs text-muted-foreground">Lot melekat pada WO; WO gabungan memakai kain yang sama untuk semua FA-nya.</div>}
    </Card>
  );
}

// ⛔ DULU kotak cari memindai SEMUA lot yang kodenya memuat kata itu (`ambilSemua`) lalu menampilkan
//    semuanya sebagai tombol — kata sependek "A" memuat ribuan roll ke browser. Kini kotak pilih
//    `PemilihCari`: server mencari, paling banyak 20 hasil; perjelas kodenya bila belum ketemu.
export default function TelusurLot() {
  const awal = new URLSearchParams(window.location.search).get("lot");
  const [lot, setLot] = useState(null);
  const [hasil, setHasil] = useState(null);

  const pilih = async (lotId) => {
    try { const r = await api.get(`/inventory/telusur-lot/lot/${lotId}`); setHasil(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Pemakaian lot gagal dimuat")); }
  };
  React.useEffect(() => { if (awal) pilih(awal); }, [awal]);
  const ganti = (l) => { setLot(l); if (l?.id) pilih(l.id); else setHasil(null); };

  return (
    <div className="max-w-4xl space-y-4" data-testid="telusur-lot-page">
      <div>
        <h1 className="text-2xl font-semibold">Telusur Lot Kain</h1>
        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">Lot kain ini dipakai FA mana. <Hint>Untuk arah sebaliknya, buka detail FA → "Lot kain yang dipakai".</Hint></p>
      </div>
      <Card className="p-3">
        <PemilihCari value={lot} onChange={ganti} cari={cariLot} label={labelLot} keterangan={ketLot}
                     placeholder="Pilih kode lot / roll" placeholderCari="Ketik kode lot / roll…"
                     testId="tl-kode" />
      </Card>
      {hasil && (
        <Card className="p-4 space-y-2" data-testid="tl-hasil">
          <div className="font-medium">Lot <span className="font-mono">{hasil.lot_code}</span> · {hasil.material}</div>
          {hasil.fa.length === 0 ? <div className="text-sm text-muted-foreground">Lot ini belum dipakai FA mana pun.</div> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground"><th>FA</th><th>WO</th><th>Pengeluaran</th><th className="text-right">Qty (WO)</th></tr></thead>
              <tbody>
                {hasil.fa.map((f, i) => (
                  <tr key={`${f.fa_id}-${i}`} className="border-t border-border">
                    <td><Link to={`/fa-orders/${f.fa_id}`} className="underline">{f.fa_number || "—"}</Link></td>
                    <td>{f.wo_number}</td>
                    <td className="text-xs">{f.issue_number}{!f.keluar && " (belum keluar)"}</td>
                    <td className="text-right tabular-nums">{f.qty_wo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  );
}
